/**
 * Partner store - Manages partner pairing, nudges, and shared goals
 * Sprint 2
 */

import { create } from 'zustand';
import { httpsCallable } from 'firebase/functions';
import { collection, query, orderBy, limit, onSnapshot, doc } from 'firebase/firestore';
import { functions, db } from '../lib/firebase';
import { getErrorMessage } from '../lib/api';
import { normalizeHousehold, normalizeHouseholdMember, normalizeNudge } from '../lib/firestoreHelpers';
import type {
  HouseholdDocument,
  HouseholdMemberDocument,
  NudgeDocument,
  NudgeTemplateId,
  SharedGoalId,
  CreatePartnerInviteResponse,
  AcceptPartnerInviteResponse,
} from '../lib/types';

interface PartnerState {
  household: HouseholdDocument | null;
  partner: HouseholdMemberDocument | null;
  myMember: HouseholdMemberDocument | null;
  recentNudges: NudgeDocument[];
  pendingInvite: { code: string; expiresAt: number } | null;
  loading: boolean;
  error: string | null;

  // Actions
  createInvite: () => Promise<void>;
  acceptInvite: (code: string) => Promise<AcceptPartnerInviteResponse>;
  sendNudge: (toUid: string, templateId: NudgeTemplateId, customText?: string) => Promise<void>;
  markNudgeViewed: (nudgeId: string) => Promise<void>;
  setSharedGoal: (goalId: SharedGoalId) => Promise<void>;
  subscribeToHousehold: (householdId: string, myUid: string) => () => void;
  reset: () => void;
}

export const usePartnerStore = create<PartnerState>((set, _get) => ({
  household: null,
  partner: null,
  myMember: null,
  recentNudges: [],
  pendingInvite: null,
  loading: false,
  error: null,

  createInvite: async () => {
    set({ loading: true, error: null });
    try {
      const createPartnerInvite = httpsCallable<unknown, CreatePartnerInviteResponse>(
        functions,
        'createPartnerInvite'
      );
      const result = await createPartnerInvite({});
      set({
        pendingInvite: { code: result.data.code, expiresAt: result.data.expiresAt },
        loading: false
      });
    } catch (err) {
      set({ error: getErrorMessage(err), loading: false });
      throw err;
    }
  },

  acceptInvite: async (code: string) => {
    set({ loading: true, error: null });
    try {
      const acceptPartnerInvite = httpsCallable<{ code: string }, AcceptPartnerInviteResponse>(
        functions,
        'acceptPartnerInvite'
      );
      const result = await acceptPartnerInvite({ code });

      // Force token refresh to pick up the new householdId custom claim
      // This is needed for Storage rules to work immediately
      const { auth } = await import('../lib/firebase');
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
      }

      set({ loading: false });
      return result.data;
    } catch (err) {
      set({ error: getErrorMessage(err), loading: false });
      throw err;
    }
  },

  sendNudge: async (toUid: string, templateId: NudgeTemplateId, customText?: string) => {
    set({ loading: true, error: null });
    try {
      const sendPartnerNudge = httpsCallable(functions, 'sendPartnerNudge');
      await sendPartnerNudge({ toUid, templateId, customText });
      set({ loading: false });
    } catch (err) {
      set({ error: getErrorMessage(err), loading: false });
      throw err;
    }
  },

  markNudgeViewed: async (nudgeId: string) => {
    try {
      const markNudgeViewedFn = httpsCallable(functions, 'markNudgeViewed');
      await markNudgeViewedFn({ nudgeId });
    } catch (err) {
      // Log sanitized error code only (no PII/health data)
      const errorCode = (err as { code?: string }).code || 'unknown';
      console.error('Failed to mark nudge viewed:', { code: errorCode });
    }
  },

  setSharedGoal: async (goalId: SharedGoalId) => {
    set({ loading: true, error: null });
    try {
      const setSharedGoalFn = httpsCallable(functions, 'setSharedGoal');
      await setSharedGoalFn({ goalId });
      set({ loading: false });
    } catch (err) {
      set({ error: getErrorMessage(err), loading: false });
      throw err;
    }
  },

  subscribeToHousehold: (householdId: string, myUid: string) => {
    // Subscribe to household document
    const householdRef = doc(db, 'households', householdId);
    const unsubHousehold = onSnapshot(householdRef, (snap) => {
      if (snap.exists()) {
        const household = normalizeHousehold(snap.id, snap.data());
        set({ household });
      }
    });

    // Subscribe to members
    const membersRef = collection(db, 'households', householdId, 'members');
    const unsubMembers = onSnapshot(membersRef, (snap) => {
      const members = snap.docs.map((d) => normalizeHouseholdMember(d.id, d.data()));
      const myMember = members.find((m) => m.uid === myUid) || null;
      const partner = members.find((m) => m.uid !== myUid) || null;
      set({ myMember, partner });
    });

    // Subscribe to recent nudges
    const nudgesRef = collection(db, 'households', householdId, 'nudges');
    const nudgesQuery = query(nudgesRef, orderBy('createdAt', 'desc'), limit(10));
    const unsubNudges = onSnapshot(nudgesQuery, (snap) => {
      const nudges = snap.docs.map((d) => normalizeNudge(d.id, d.data()));
      set({ recentNudges: nudges });
    });

    return () => {
      unsubHousehold();
      unsubMembers();
      unsubNudges();
    };
  },

  reset: () => {
    set({
      household: null,
      partner: null,
      myMember: null,
      recentNudges: [],
      pendingInvite: null,
      loading: false,
      error: null,
    });
  },
}));

// ==================== Performance-Optimized Selectors ====================
// Use these hooks for granular subscriptions to prevent unnecessary re-renders

/** Subscribe to household data only */
export const useHousehold = () => usePartnerStore(state => state.household);

/** Subscribe to partner member only */
export const usePartner = () => usePartnerStore(state => state.partner);

/** Subscribe to current user's member data only */
export const useMyMember = () => usePartnerStore(state => state.myMember);

/** Subscribe to recent nudges only */
export const useRecentNudges = () => usePartnerStore(state => state.recentNudges);

/** Subscribe to pending invite only */
export const usePendingInvite = () => usePartnerStore(state => state.pendingInvite);

/** Subscribe to partner loading state only */
export const usePartnerLoading = () => usePartnerStore(state => state.loading);

/** Subscribe to partner error state only */
export const usePartnerError = () => usePartnerStore(state => state.error);

/** Subscribe to pairing status */
export const useIsPaired = () => usePartnerStore(state => state.partner !== null);
